import flatten from '@opten/array/flatten';
import { Component } from '@opten/compiler/metadata/component';
import addClass from '@opten/dom/add-class';
import getParentOrSelfWithClass from '@opten/dom/get-parent-or-self-with-class';
import getParentWithClass from '@opten/dom/get-parent-with-class';
import removeClass from '@opten/dom/remove-class';
import { copy } from '@opten/object/copy';
import { Renderer } from '@opten/renderer';
import { DefaultConverter } from '@opten/renderer/converters/default.converter';
import { IfConverter } from '@opten/renderer/converters/if.converter';
import { InputConverter } from '@opten/renderer/converters/input.converter';
import { RepeatInnerConverter } from '@opten/renderer/converters/repeat.inner.converter';
import { queryStringAsObject } from '@opten/url/query-string-as-object';
import { combineLatest, Observable, of as observableOf, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { CurrencyConverter } from '../../utilities/currency.converter';
import { validateInput, validateModel } from '../core/validators';
import {
	Basket,
	BasketItem,
	BasketPricesForOrderTypes,
	BasketProductItem,
	BasketProductVariantItem,
	PaymentMethod
} from '../models/basket';
import { Location, User } from '../models/customer';
import { Address } from '../models/profile';
import { isPartner } from '../models/roles';
import { basketStorage, shopService } from '../shop/shop-context';
import { BaseBasket, BaseBasketOptions } from './basket-base';

export interface BasketComponentOptions extends BaseBasketOptions {
	basket?: {
		basketEmpty: string;
		productVariantQuantityUpdate: string;
		productVariantRemove: string;
		basketSubmit: string;
		locationSelect: string;
	};
	hidden: string;
}

@Component({ className: 'js-basket' })
export class BasketComponent extends BaseBasket {
	options: BasketComponentOptions = {
		selectors: {
			location: 'js-location',
			product: 'js-product-basket',
			productImage: 'js-product-image',
			productVariant: 'js-product-variant',
			productVariantPrice: 'js-product__price',
			productVariantQuantity: 'js-product__quantity'
		},
		basket: {
			basketEmpty: 'js-basket-empty',
			basketSubmit: 'js-basket__submit',
			locationSelect: 'js-location__select',
			productVariantRemove: 'js-product__quantity--remove',
			productVariantQuantityUpdate: 'js-product__quantity--update'
		},
		target: 'data-target',
		id: 'data-id',
		name: 'data-name',
		disabled: '--disabled',
		hidden: '--hidden'
	};

	private _basket: Basket = {};
	private submitButtons: HTMLElement[];
	private orderIsSubmitting = false;
	private result: HTMLElement;
	private loader: HTMLElement;
	private currencyConverter: CurrencyConverter;
	private renderer: Renderer;
	private locations: any[];
	private isGuest: boolean;
	private user: User;
	successfullySubmitted: boolean;

	constructor(private element: HTMLElement) {
		super(element);

		this.loader = this.element.querySelector('.js-basket-loader');
		this.result = this.element.querySelector('.js-basket-result');
		this.currencyConverter = new CurrencyConverter();

		this.renderer = new Renderer([IfConverter, DefaultConverter, InputConverter, RepeatInnerConverter]);

		const locationSelectElements = this.basketElement
			? (Array.from(this.basketElement.getElementsByClassName(this.options.basket.locationSelect)) as HTMLSelectElement[])
			: null;

		if (locationSelectElements && locationSelectElements.length > 0) {
			locationSelectElements.forEach(locationSelectElement =>
				this.element.addEventListener('finished.opten.renderer', () => {
					locationSelectElement = this.reinitLocationSelect(this.element, locationSelectElement);
				})
			);
		}

		this.submitButtons = Array.from(this.element.querySelectorAll(`.${this.options.basket.basketSubmit}`));
		if (this.submitButtons) {
			this.submitButtons.forEach(b => b.addEventListener('click', e => this.submitOrder(e)));
		}
	}

	onInit(): void {
		this.showLoader();

		const queryParams = queryStringAsObject(window.location.search);
		if (queryParams && queryParams.status) {
			switch (queryParams.status) {
				case 'success':
					this.handleSuccess();
					break;
				case 'failure':
					this.handleError();
					break;
				default:
					break;
			}
		}

		combineLatest(this.shopContext.onAuthenticationChanged, this.shopContext.onIsGuest)
			.pipe(
				filter(([usr, isGuest]) => !!usr && isGuest !== null),
				distinctUntilChanged(([userA, isGuestA], [userB, isGuestB]) => userA.id === userB.id),
				switchMap(([usr, isGuest]) => {
					this.user = usr;
					this.isGuest = isGuest;
					this.locations = this.getLocationAddressPreviews(usr);
					document
						.querySelectorAll<HTMLInputElement>(`[name="${this.isGuest ? 'guest' : 'user'}PaymentMethod"][value="invoice"]`)
						.forEach(box => (box.checked = true));
					return basketStorage.onBasketUpdate;
				}),
				// filter(basket => !!basket),
				switchMap(basket => {
					this.showButtonLoader();

					this._basket = basket;
					if (this._basket) {
						this._basket.shippingLocations = this.locations;
						this._basket.isGuest = this.isGuest;

						if (this.isGuest === false && (!this._basket.customerName || this._basket.customerName.length === 0)) {
							this._basket.customerName = this.user.displayName;
						}

						document
							.querySelectorAll<HTMLInputElement>(
								`[name="${this.isGuest ? 'guest' : 'user'}ShippingMethod"][value="${this._basket.shippingMethod}"]`
							)
							.forEach(box => (box.checked = true));

						this.renderer.render(this.element, this._basket, this);
					} else {
						this.hideLoader();
					}

					return combineLatest([shopService.getBasketView(this._basket), shopService.getTotalPrice(this._basket)]);
				}),
				catchError(error => {
					console.error('error getting totalprice or basketview', error);
					return of(null);
				})
			)
			.subscribe(([view, prices]) => {
				this.result.innerHTML = view;

				if (this.successfullySubmitted) {
					addClass(document.querySelector('.js-basket-message-empty'), 'd-none');
				}

				this.renderer.render(this.element, this._basket, this);

				if (prices) {
					this.renderPrices(prices);
				}

				this.reinitEventListeners();
				this.hideButtonLoader();
				this.hideLoader();
			});
	}

	private renderPrices(prices: BasketPricesForOrderTypes) {
		const subtotals = this.element.querySelectorAll('.js-basket-subtotal');
		subtotals.forEach(sub => {
			sub.innerHTML = this.currencyConverter.getFormattedString(prices[sub.getAttribute('data-order-type')].goodsCost);
		});

		const shippings = this.element.querySelectorAll('.js-basket-shipping');
		if (this._basket.shippingMethod === '0') {
			shippings.forEach(sub => {
				addClass(sub.parentElement, 'd-flex');
				removeClass(sub.parentElement, 'd-none');

				const price = prices[sub.getAttribute('data-order-type')];
				if (price.specialFreightCostCalculation) {
					if (price.freightCost > 0) {
						sub.previousElementSibling.innerHTML = sub.getAttribute('data-calc-exclusive');
					} else {
						sub.previousElementSibling.innerHTML = sub.getAttribute('data-calc-inclusive');
					}
				} else if (price.freightCost > 0) {
					const text = sub
						.getAttribute('data-calc-normal')
						.replace(
							'{0}',
							`<strong>${this.currencyConverter.getFormattedString(price.priceDifferenceToFreeShipping)}</strong>`
						);
					sub.previousElementSibling.innerHTML += `<span class="c-basket__price__info">${text}</span>`;
				}

				sub.innerHTML = this.currencyConverter.getFormattedString(price.freightCost);
			});
		} else {
			shippings.forEach(o => {
				addClass(o.parentElement, 'd-none');
				removeClass(o.parentElement, 'd-flex');
			});
		}

		const totalPrices = Object.values(prices);

		const priceWithoutVat = this.element.querySelector('.js-basket-price-without-vat');
		const priceShipping = this.element.querySelector('.js-basket-price-shipping');
		const priceVat = this.element.querySelector('.js-basket-price-vat');
		const priceTotal = this.element.querySelector('.js-basket-price-total');

		if (priceShipping) {
			if (this._basket.shippingMethod === '0') {
				removeClass(priceShipping.parentElement, 'd-none');
			} else {
				addClass(priceShipping.parentElement, 'd-none');
			}
			priceShipping.innerHTML = this.currencyConverter.getFormattedString(totalPrices.reduce((a, c) => (a += c.freightCost), 0));
		}

		if (priceWithoutVat) {
			priceWithoutVat.innerHTML = this.currencyConverter.getFormattedString(totalPrices.reduce((a, c) => (a += c.goodsCost), 0));
		}
		if (priceVat) {
			priceVat.innerHTML = this.currencyConverter.getFormattedString(totalPrices.reduce((a, c) => (a += c.vat), 0));
		}
		if (priceTotal) {
			priceTotal.innerHTML = this.currencyConverter.getFormattedString(totalPrices.reduce((a, c) => (a += c.totalPrice), 0));
		}
	}

	private reinitEventListeners() {
		const quantities = this.element.querySelectorAll(`.${this.options.selectors.productVariantQuantity}`);
		quantities.forEach((quantity: HTMLElement) => quantity.addEventListener('change', e => this.updateQuantity(e, quantity)));

		const removeVariants = this.element.querySelectorAll(`.${this.options.basket.productVariantRemove}`);
		removeVariants.forEach((remove: HTMLElement) => remove.addEventListener('click', e => this.updateQuantity(e, remove, true)));
	}

	updateQuantity(e: Event, quantity: HTMLElement, remove = false): void {
		this.showButtonLoader();
		const variant = getParentOrSelfWithClass(quantity, 'js-product-variant');
		const product = getParentOrSelfWithClass(variant, 'js-product');
		const location = getParentOrSelfWithClass(product, 'js-location');

		const item = this._basket.items
			.find(o => o.location.id === +location.getAttribute('data-id') && o.orderType === product.getAttribute('data-order-type'))
			.products.find(o => o.id === product.getAttribute('data-id'))
			.variants.find(o => o.id === variant.getAttribute('data-id'));

		this.updateProductVariant(e, item, remove);
	}

	updateBasketItemInput(event: Event, basketItem: BasketItem) {
		const element = (event.currentTarget || event.target) as HTMLInputElement;
		let orderType = !!basketItem ? basketItem.orderType : null;
		let locationId = !!basketItem && !!basketItem.location ? basketItem.location.id : null;

		if (!orderType) {
			orderType = element.getAttribute('data-order-type');
		}

		if (!locationId) {
			locationId = +element.getAttribute('data-location-id');
		}

		if (validateInput(element, this.submitButtons)) {
			basketStorage.get().subscribe(basketElement => {
				const item = basketElement.items.find(o => o.location.id === locationId && o.orderType === orderType);
				if (item) {
					switch (element.type) {
						case 'checkbox':
							item.options[element.name] = element.checked;
							break;
						default:
							item.options[element.name] = element.value;
					}
					this.updateFullBasket(basketElement);
				}
			});
		}
	}

	updateBasketInput(event: Event, basketElement: Basket) {
		const element = (event.currentTarget || event.target) as HTMLInputElement;
		let updatedBasket = false;
		if (this._basket.hasOwnProperty(element.name)) {
			switch (element.type) {
				case 'checkbox':
					this._basket[element.name] = element.checked;
					updatedBasket = true;
					break;
				default:
					this._basket[element.name] = element.value;
					updatedBasket = true;
			}
		} else if (element.name.indexOf('centralWarehouse') > -1) {
			const newBasketWarehouse = this.updateBasketInputCentralWarehouse(element, this._basket, updatedBasket);
			this._basket = newBasketWarehouse.basket;
			updatedBasket = newBasketWarehouse.updated;
		} else if (element.name.indexOf('guestAddresses') > -1) {
			const newBasketGuestAdresses = this.updateBasketInputGuestAdresses(element, this._basket, updatedBasket);
			this._basket = newBasketGuestAdresses.basket;
			updatedBasket = newBasketGuestAdresses.updated;
		}

		validateInput(element, this.submitButtons);
		this.updateFullBasket(this._basket, updatedBasket);
	}

	updatePaymentMethod(event: Event, method: PaymentMethod) {
		const element = (event.currentTarget || event.target) as HTMLInputElement;
		validateInput(element, this.submitButtons);

		// this._basket.paymentMethods.forEach(o => (o.selected = false));
		// this._basket.paymentMethods.find(o => o.value === method.value).selected = true;

		this._basket.paymentMethod = element.value;
		this.updateFullBasket(this._basket, true);
	}

	updateShippingMethod(event: Event) {
		const element = (event.currentTarget || event.target) as HTMLInputElement;
		validateInput(element, this.submitButtons);
		this._basket.shippingMethod = element.value;
		this.updateFullBasket(this._basket, true);
	}

	updateProductVariant(event: Event, productVariant: BasketProductVariantItem, removeVariant?: boolean) {
		event.preventDefault();
		const element = event.currentTarget || event.target;
		if (element) {
			const product = getParentWithClass(element as HTMLElement, this.options.selectors.product);

			if (product) {
				const convertedProduct: BasketProductItem = this.convertProduct(product);
				const locationElement = getParentWithClass(element as HTMLElement, this.options.selectors.location);
				const location = this.getLocation(locationElement);
				if (convertedProduct && location) {
					convertedProduct.variants = convertedProduct.variants.filter(v => v.id === productVariant.id);
					if (removeVariant) {
						convertedProduct.variants.forEach(v => (v.quantity = 0));
					}
					basketStorage.updateProductVariant(convertedProduct, location).subscribe(
						basket => {
							this.shopContext.basketUpdated(basket);
						},
						error => {
							return false;
						}
					);
				}
			}
		}
	}

	removeProductVariant(event: Event, productVariant: BasketProductVariantItem) {
		this.updateProductVariant(event, productVariant, true);
	}

	submitOrder(event: Event) {
		event.preventDefault();
		const submitButton = (event.currentTarget || event.target) as HTMLElement;
		addClass(submitButton, this.options.disabled);

		this.hideMessages();
		this.showButtonLoader();

		if (this.orderIsSubmitting === false) {
			this.orderIsSubmitting = true;

			basketStorage
				.get()
				.pipe(
					switchMap(basket => {
						return combineLatest(this.validBasketModel(), basketStorage.getOrderModel(basket));
					})
				)
				.subscribe(([isValid, order]) => {
					if (isValid) {
						shopService.order(order).subscribe(response => {
							if (response.hasError) {
								if (response.errorMessage === 'termsNotAccepted') {
									const privacyPolicy = this.basketElement.querySelector('name="termsAndConditions"');
									if (privacyPolicy) {
										validateInput(privacyPolicy as HTMLInputElement, this.submitButtons);
									}
									this.handleError(false);
								} else {
									this.handleError();
								}
							} else if (response.redirectUrl) {
								window.location.href = response.redirectUrl;
							} else {
								this.handleSuccess();
							}
							this.orderIsSubmitting = false;
							this.hideButtonLoader();
						});
					} else {
						this.orderIsSubmitting = false;
						this.handleError(false);
						this.hideButtonLoader();
					}
				});
		}
	}

	private validBasketModel(): Observable<boolean> {
		let fieldsToValidate = Array.from(this.basketElement.querySelectorAll('[required]')) as HTMLInputElement[];
		if (fieldsToValidate) {
			return combineLatest(this.shopContext.onAuthenticationChanged, this.shopContext.onIsGuest).pipe(
				filter(([user, isGuest]) => !!user && isGuest !== null),
				switchMap(([user, isGuest]) => {
					if (isGuest || isPartner(user)) {
						fieldsToValidate = fieldsToValidate
							.filter(o => o.name.indexOf('centralWarehouseAddress') < 0)
							.filter(o => !o.name.startsWith('userShippingMethod') && !o.name.startsWith('userPaymentMethod'));
					} else {
						fieldsToValidate = fieldsToValidate
							.filter(o => o.name.indexOf('guestAddresses') < 0)
							.filter(o => !o.name.startsWith('guestShippingMethod') && !o.name.startsWith('guestPaymentMethod'));
						if (this._basket.centralWarehouse === false) {
							fieldsToValidate = fieldsToValidate.filter(o => o.name.indexOf('centralWarehouseAddress') < 0);
						}
					}

					return observableOf(validateModel(fieldsToValidate, this.submitButtons));
				})
			);
		} else {
			return of(true);
		}
	}

	private handleSuccess() {
		this.showSuccess();

		this._basket.submitSuccess = true;
		this._basket.submitError = false;
		this._basket.items = [];
		window.scrollTo(0, 0);

		this.shopContext.finalizeBasket().subscribe(() => {
			this.showSuccess();
		});

		this.shopContext.basketUpdated(this._basket);
	}

	private handleError(validateFullBasket = true) {
		this.showError();

		if (validateFullBasket) {
			this.validBasketModel();
		}
		this._basket.submitSuccess = false;
		this._basket.submitError = true;
		this.submitButtons.forEach(o => removeClass(0, this.options.disabled));
	}

	private updateCentralWarehouse(locationId: string) {
		this._basket.centralWarehouseAddress = this._basket.shippingLocations.find(o => o.id === +locationId);
		this.updateFullBasket(this._basket, true);
	}

	private updateCentralWarehouseAddress(location: Location) {
		this._basket.centralWarehouseAddress = {
			street: location.addresses[0].street,
			zip: location.addresses[0].zip,
			location: location.addresses[0].location,
			addresses: location.id
		};

		if (this._basket.shippingLocations && this._basket.shippingLocations.length > 0) {
			this._basket.shippingLocations.forEach(shippingLocation => {
				shippingLocation.selected = shippingLocation.id === location.id;
			});
		}
	}

	private updateFullBasket(basketElement: Basket, updatedBasket = true): void {
		if (updatedBasket) {
			basketStorage.updateFullBasket(basketElement).subscribe(basket => {
				this.shopContext.basketUpdated(basket);
			});
		}
	}

	private updateBasketInputCentralWarehouse(
		element: HTMLInputElement,
		basketElement: Basket,
		updatedBasket: boolean
	): {
		basket: Basket;
		updated: boolean;
	} {
		const centralWarehouseAddress = this.updateCentralWarehouseElement(
			basketElement,
			'centralWarehouseAddress',
			element,
			updatedBasket
		);
		basketElement = centralWarehouseAddress.basket;
		updatedBasket = centralWarehouseAddress.updated;
		const updatedCentralWarehouseOptions = this.updateCentralWarehouseElement(
			basketElement,
			'centralWarehouseOptions',
			element,
			updatedBasket
		);
		basketElement = updatedCentralWarehouseOptions.basket;
		updatedBasket = updatedCentralWarehouseOptions.updated;

		if (element.name === 'centralWarehouseAddress.addresses' && basketElement.shippingLocations) {
			// for (let i = 0; i < basketElement.shippingLocations.length; i++) {
			// 	basketElement.shippingLocations[i].selected = basketElement.shippingLocations[i].id.toString() === element.value;
			// }
			// this._basket = basketElement;
			this.updateCentralWarehouse(element.value);
		}

		return {
			basket: basketElement,
			updated: updatedBasket
		};
	}

	private updateBasketInputGuestAdresses(
		element: HTMLInputElement,
		basketElement: Basket,
		updatedBasket: boolean
	): {
		basket: Basket;
		updated: boolean;
	} {
		let guestAddresses: {
			basket: Basket;
			updated: boolean;
		} = {
			basket: basketElement,
			updated: updatedBasket
		};
		if (element.name.indexOf('shippingIsBillingAddress') > -1) {
			if (element.checked) {
				guestAddresses.basket.items[0].location.shippingAddress = copy(basketElement.items[0].location.billingAddress) as Address;
				guestAddresses.basket.items[0].location.shippingIsBillingAddress = true;
			} else {
				guestAddresses.basket.items[0].location.shippingIsBillingAddress = false;
			}
			guestAddresses.updated = true;
		} else if (element.name.indexOf('guestPhone') > -1) {
			guestAddresses.basket.items[0].location.guestPhone = element.value;
			guestAddresses.updated = true;
		} else if (element.name.indexOf('guestEmail') > -1) {
			guestAddresses.basket.items[0].location.guestEmail = element.value;
			guestAddresses.updated = true;
		} else {
			const billingAddress = this.updateGuestAdresses(
				basketElement,
				'guestAddresses.billingAddress',
				element,
				updatedBasket,
				'billingAddress'
			);
			basketElement = billingAddress.basket;
			updatedBasket = billingAddress.updated;
			if (
				billingAddress.updated &&
				basketElement.items[0].location.shippingIsBillingAddress !== undefined &&
				basketElement.items[0].location.shippingIsBillingAddress
			) {
				guestAddresses = this.updateGuestAdresses(
					basketElement,
					'guestAddresses.billingAddress',
					element,
					false,
					'shippingAddress'
				);
			} else {
				guestAddresses = this.updateGuestAdresses(
					basketElement,
					'guestAddresses.shippingAddress',
					element,
					updatedBasket,
					'shippingAddress'
				);
			}
		}
		return {
			basket: guestAddresses.basket,
			updated: guestAddresses.updated
		};
	}

	private updateCentralWarehouseElement(
		basketElement: Basket,
		objectname: string,
		element: HTMLInputElement,
		updatedBasket: boolean
	): {
		basket: Basket;
		updated: boolean;
	} {
		if (!updatedBasket) {
			if (
				element.name.indexOf(objectname) === 0 &&
				basketElement.hasOwnProperty(objectname) &&
				basketElement[objectname].hasOwnProperty(element.name.substring((objectname + '.').length))
			) {
				const optionName = element.name.substring((objectname + '.').length);
				switch (element.type) {
					case 'checkbox':
						basketElement[objectname][optionName] = element.checked;
						updatedBasket = true;
						break;
					default:
						basketElement[objectname][optionName] = element.value;
						updatedBasket = true;
				}
			}
		}
		return {
			basket: basketElement,
			updated: updatedBasket
		};
	}

	private updateGuestAdresses(
		basketElement: Basket,
		objectname: string,
		element: HTMLInputElement,
		updatedBasket: boolean,
		addressToUpdate: string
	): {
		basket: Basket;
		updated: boolean;
	} {
		if (!updatedBasket) {
			if (
				element.name.indexOf(objectname) === 0 &&
				basketElement.items[0].location.hasOwnProperty(addressToUpdate) &&
				basketElement.items[0].location[addressToUpdate].hasOwnProperty(element.name.substring((objectname + '.').length))
			) {
				const optionName = element.name.substring((objectname + '.').length);
				switch (element.type) {
					case 'checkbox':
						basketElement.items[0].location[addressToUpdate][optionName] = element.checked;
						updatedBasket = true;
						break;
					default:
						basketElement.items[0].location[addressToUpdate][optionName] = element.value;
						updatedBasket = true;
				}
			}
		}
		return {
			basket: basketElement,
			updated: updatedBasket
		};
	}

	private showLoader() {
		removeClass(this.loader, 'd-none');
		addClass(this.result, 'd-none');
	}

	private hideLoader() {
		addClass(this.loader, 'd-none');
		removeClass(this.result, 'd-none');
	}

	private showButtonLoader() {
		this.submitButtons.forEach(b => addClass(b, 'is-loading'));
	}

	private hideButtonLoader() {
		this.submitButtons.forEach(b => removeClass(b, 'is-loading'));
	}

	private showSuccess() {
		this.successfullySubmitted = true;
		const successMessage = this.element.querySelector('.js-basket-message-success');
		const errorMessages = Array.from(this.element.querySelectorAll('.js-basket-message-error'));

		if (successMessage) {
			removeClass(successMessage, 'd-none');
		}
		if (errorMessages && errorMessages.length > 0) {
			errorMessages.forEach(message => addClass(message, 'd-none'));
		}
	}

	private showError() {
		const successMessage = this.element.querySelector('.js-basket-message-success');
		const errorMessages = Array.from(this.element.querySelectorAll('.js-basket-message-error'));

		if (successMessage) {
			addClass(successMessage, 'd-none');
		}
		if (errorMessages && errorMessages.length > 0) {
			errorMessages.forEach(message => removeClass(message, 'd-none'));
		}
	}

	private hideMessages() {
		const successMessage = this.element.querySelector('.js-basket-message-success');
		const errorMessages = Array.from(this.element.querySelectorAll('.js-basket-message-error'));

		if (successMessage) {
			addClass(successMessage, 'd-none');
		}
		if (errorMessages && errorMessages.length > 0) {
			errorMessages.forEach(message => addClass(message, 'd-none'));
		}
	}

	private getLocationAddressPreviews(user: User) {
		return flatten(
			user.contractualPartner.multiLocations.map(ml =>
				ml.locations.map(l => {
					return {
						id: l.id,
						value: l.addressPreview,
						street: l.addresses[0].street,
						location: l.addresses[0].location,
						zip: l.addresses[0].zip
					};
				})
			)
		);
	}
}
